import React from 'react';
import classNames from 'classnames';
import styles from './ProgressBar.module.scss';

export interface ProgressBarProps {
  ariaLabel?: string;
  progress: number;
  showEdgeLabels?: boolean;
  fullWidth?: boolean;
  className?: string;
  showValue?: boolean;
  valueSuffix?: string;
  /**
   * Значение для отображения справа (не обязательно равно `progress`).
   * Удобно, когда бар заполнен по одной шкале, а значение справа по другой.
   */
  displayValue?: number;
  /**
   * Количество знаков после запятой для отображаемого значения.
   * Если не задано — округляем до целого.
   */
  valuePrecision?: number;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  ariaLabel,
  showEdgeLabels = false,
  fullWidth = false,
  className,
  showValue = false,
  valueSuffix = '',
  displayValue,
  valuePrecision,
}) => {
  const clamped = Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;

  const valueForDisplay = displayValue ?? clamped;

  const formattedValue = (() => {
    if (!showValue) return null;
    if (!Number.isFinite(valueForDisplay)) return null;
    if (valuePrecision !== undefined) return valueForDisplay.toFixed(valuePrecision);
    return Math.round(valueForDisplay).toString();
  })();

  const valueText = formattedValue !== null ? `${formattedValue}${valueSuffix}` : null;

  return (
    <div className={classNames(styles['progress'], fullWidth && styles['progress--full-width'], className)}>
      <div className={styles['progress-row']}>
        <div
          className={styles['progress-bar-track']}
          role="progressbar"
          aria-label={ariaLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={clamped}
        >
          <div className={styles['progress-bar-fill']} style={{ width: `${clamped}%` }} />
        </div>
        {showValue && <span className={styles['progress-value']}>{valueText}</span>}
      </div>

      {showEdgeLabels && (
        <div className={styles['progress-edge-labels']}>
          <span>{`0${valueSuffix}`}</span>
          <span>{`100${valueSuffix}`}</span>
        </div>
      )}
    </div>
  );
};

